import { useCallback, useEffect, useState } from 'react';
import { getBalance, AccountNode } from '../services/apiService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ObrigacaoItem {
  funcionario: string;
  balance: number;
  full_path: string;
}

export interface TipoCompetencia {
  competencia: string;
  balance: number;
  status: 'pago' | 'pendente' | 'parcial' | 'credito';
  hasDivergence: boolean;
  items: ObrigacaoItem[];
}

export interface TipoGroup {
  tipo: string;
  full_path: string;
  totalBalance: number;
  competencias: TipoCompetencia[];
}

export interface EmprestimoSummary {
  banco: string;
  contrato: string;
  totalBalance: number;
  parcelasRestantes: number;
  full_path: string;
}

export interface DASRow {
  competencia: string;
  balance: number;
  status: 'pago' | 'pendente' | 'credito';
  full_path: string;
}

export interface AdiantamentoCliente {
  cliente: string;
  balance: number;
  full_path: string;
}

export interface ProvisaoCliente {
  cliente: string;
  balance: number;
  full_path: string;
  competencias: { competencia: string; balance: number; full_path: string }[];
}

export interface CategorySummary {
  name: string;
  full_path: string;
  total: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function brl(node: AccountNode): number {
  return node.balances?.BRL ?? node.balances?.['R$'] ?? 0;
}

function findChild(nodes: AccountNode[], name: string): AccountNode | undefined {
  return nodes.find((n) => n.account === name);
}

const COMPETENCIA_RE = /^\d{4}-\d{2}$/;

// ---------------------------------------------------------------------------
// Pure transformation functions
// ---------------------------------------------------------------------------

/**
 * Groups Passivo:Obrigações by tipo, then by competência within each tipo.
 *
 * Structure: Obrigações → {Tipo} → {YYYY-MM} → {Funcionário}
 * DAS and DARF are excluded (handled separately).
 */
export function groupObrigacoesByTipo(obrigacoesNode: AccountNode | undefined): TipoGroup[] {
  if (!obrigacoesNode) return [];

  const SKIP_TIPOS = ['DAS', 'DARF'];
  const groups: TipoGroup[] = [];

  for (const tipoNode of obrigacoesNode.children) {
    if (SKIP_TIPOS.includes(tipoNode.account)) continue;

    const tipoBalance = brl(tipoNode);
    const competencias: TipoCompetencia[] = [];

    for (const child of tipoNode.children) {
      if (!COMPETENCIA_RE.test(child.account)) continue;

      const balance = brl(child);
      const absBalance = Math.abs(balance);

      const items: ObrigacaoItem[] = [];
      if (child.children.length > 0) {
        for (const funcNode of child.children) {
          items.push({
            funcionario: funcNode.account,
            balance: brl(funcNode),
            full_path: funcNode.full_path,
          });
        }
        items.sort((a, b) => a.funcionario.localeCompare(b.funcionario));
      }

      // In Passivo: negative = devendo, positive = crédito (pagou a mais), ~0 = pago
      let status: TipoCompetencia['status'];
      if (absBalance < 0.01) {
        status = 'pago';
      } else if (balance > 0) {
        status = 'credito';
      } else if (items.length > 0) {
        const hasPaid = items.some((i) => Math.abs(i.balance) < 0.01);
        const hasUnpaid = items.some((i) => Math.abs(i.balance) >= 0.01);
        status = hasPaid && hasUnpaid ? 'parcial' : 'pendente';
      } else {
        status = 'pendente';
      }

      const hasDivergence = absBalance >= 0.01 && absBalance <= 5.0;

      competencias.push({
        competencia: child.account,
        balance,
        status,
        hasDivergence,
        items,
      });
    }

    // Skip tipos with no competências
    if (competencias.length === 0) continue;

    // Sort competências desc
    competencias.sort((a, b) => b.competencia.localeCompare(a.competencia));

    groups.push({
      tipo: tipoNode.account,
      full_path: tipoNode.full_path,
      totalBalance: tipoBalance,
      competencias,
    });
  }

  // Sort by absolute balance desc (biggest obligations first)
  groups.sort((a, b) => Math.abs(b.totalBalance) - Math.abs(a.totalBalance));
  return groups;
}

/**
 * Summarize Passivo:Empréstimo children by banco→contrato.
 */
export function summarizeEmprestimos(emprestimoNode: AccountNode | undefined): EmprestimoSummary[] {
  if (!emprestimoNode) return [];

  const summaries: EmprestimoSummary[] = [];

  for (const bancoNode of emprestimoNode.children) {
    const bancoBalance = brl(bancoNode);
    if (Math.abs(bancoBalance) < 0.01) continue;

    if (bancoNode.children.length === 0) {
      summaries.push({
        banco: bancoNode.account,
        contrato: '-',
        totalBalance: bancoBalance,
        parcelasRestantes: 0,
        full_path: bancoNode.full_path,
      });
    } else {
      for (const contratoNode of bancoNode.children) {
        const totalBalance = brl(contratoNode);
        if (Math.abs(totalBalance) < 0.01) continue;

        let parcelasRestantes = 0;
        if (contratoNode.children.length > 0) {
          for (const parcela of contratoNode.children) {
            if (Math.abs(brl(parcela)) >= 0.01) {
              parcelasRestantes++;
            }
          }
        }

        summaries.push({
          banco: bancoNode.account,
          contrato: contratoNode.account,
          totalBalance,
          parcelasRestantes,
          full_path: contratoNode.full_path,
        });
      }
    }
  }

  summaries.sort((a, b) => Math.abs(b.totalBalance) - Math.abs(a.totalBalance));
  return summaries;
}

/**
 * Extract DAS rows from Passivo:Obrigações:DAS.
 */
export function extractDAS(obrigacoesNode: AccountNode | undefined): DASRow[] {
  if (!obrigacoesNode) return [];

  const dasNode = findChild(obrigacoesNode.children, 'DAS');
  if (!dasNode) return [];

  const rows: DASRow[] = [];
  for (const child of dasNode.children) {
    const balance = brl(child);
    let dasStatus: DASRow['status'];
    if (Math.abs(balance) < 0.01) dasStatus = 'pago';
    else if (balance > 0) dasStatus = 'credito';
    else dasStatus = 'pendente';

    rows.push({
      competencia: child.account,
      balance,
      status: dasStatus,
      full_path: child.full_path,
    });
  }

  rows.sort((a, b) => b.competencia.localeCompare(a.competencia));
  return rows;
}

/**
 * Extract Passivo:Adiantamentos de Clientes children by client.
 */
export function extractAdiantamentos(passivoChildren: AccountNode[]): AdiantamentoCliente[] {
  const node = findChild(passivoChildren, 'Adiantamentos de Clientes');
  if (!node) return [];

  const results: AdiantamentoCliente[] = [];
  for (const child of node.children) {
    const balance = brl(child);
    if (Math.abs(balance) < 0.01) continue;
    results.push({
      cliente: child.account,
      balance,
      full_path: child.full_path,
    });
  }
  results.sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance));
  return results;
}

/**
 * Extract Passivo:Provisão a Faturar children by client with competências.
 */
export function extractProvisaoFaturar(passivoChildren: AccountNode[]): ProvisaoCliente[] {
  const node = findChild(passivoChildren, 'Provisão a Faturar');
  if (!node) return [];

  const results: ProvisaoCliente[] = [];
  for (const child of node.children) {
    const balance = brl(child);
    const competencias: ProvisaoCliente['competencias'] = [];
    for (const comp of child.children) {
      if (!COMPETENCIA_RE.test(comp.account)) continue;
      const compBal = brl(comp);
      if (Math.abs(compBal) < 0.01) continue;
      competencias.push({
        competencia: comp.account,
        balance: compBal,
        full_path: comp.full_path,
      });
    }
    competencias.sort((a, b) => b.competencia.localeCompare(a.competencia));
    if (Math.abs(balance) < 0.01 && competencias.length === 0) continue;
    results.push({
      cliente: child.account,
      balance,
      full_path: child.full_path,
      competencias,
    });
  }
  results.sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance));
  return results;
}

/**
 * Extract everything in Passivo that is NOT Obrigações or Empréstimo.
 */
export function extractOutros(
  passivoChildren: AccountNode[],
  knownNames: string[],
): CategorySummary[] {
  const results: CategorySummary[] = [];
  for (const node of passivoChildren) {
    if (knownNames.includes(node.account)) continue;
    const total = brl(node);
    if (Math.abs(total) < 0.01) continue;
    results.push({ name: node.account, full_path: node.full_path, total });
  }
  results.sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  return results;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface ContasPagarData {
  obrigacoes: TipoGroup[];
  emprestimos: EmprestimoSummary[];
  das: DASRow[];
  adiantamentos: AdiantamentoCliente[];
  provisaoFaturar: ProvisaoCliente[];
  outros: CategorySummary[];
  totals: {
    obrigacoes: number;
    emprestimos: number;
    das: number;
    adiantamentos: number;
    provisaoFaturar: number;
    outros: number;
    grandTotal: number;
  };
}

export function useContasPagarData() {
  const [data, setData] = useState<ContasPagarData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getBalance({ account: 'Passivo' });

      // API returns [Passivo] as root — we need its children
      const passivoNode = result.accounts[0];
      const passivoChildren = passivoNode?.children || [];

      const obrigacoesNode = findChild(passivoChildren, 'Obrigações');
      const emprestimoNode = findChild(passivoChildren, 'Empréstimo');

      const obrigacoes = groupObrigacoesByTipo(obrigacoesNode);
      const emprestimos = summarizeEmprestimos(emprestimoNode);
      const das = extractDAS(obrigacoesNode);
      const adiantamentos = extractAdiantamentos(passivoChildren);
      const provisaoFaturar = extractProvisaoFaturar(passivoChildren);
      const outros = extractOutros(passivoChildren, ['Obrigações', 'Empréstimo', 'Adiantamentos de Clientes', 'Provisão a Faturar']);

      const totObrigacoes = obrigacoes.reduce((s, g) => s + g.totalBalance, 0);
      const totEmprestimos = emprestimos.reduce((s, r) => s + r.totalBalance, 0);
      const totDAS = das.reduce((s, r) => s + r.balance, 0);
      const totAdiantamentos = adiantamentos.reduce((s, r) => s + r.balance, 0);
      const totProvisaoFaturar = provisaoFaturar.reduce((s, r) => s + r.balance, 0);
      const totOutros = outros.reduce((s, r) => s + r.total, 0);

      setData({
        obrigacoes,
        emprestimos,
        das,
        adiantamentos,
        provisaoFaturar,
        outros,
        totals: {
          obrigacoes: totObrigacoes,
          emprestimos: totEmprestimos,
          das: totDAS,
          adiantamentos: totAdiantamentos,
          provisaoFaturar: totProvisaoFaturar,
          outros: totOutros,
          grandTotal: totObrigacoes + totEmprestimos + totDAS + totAdiantamentos + totProvisaoFaturar + totOutros,
        },
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao carregar contas a pagar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}
